// Ours: the footer logo that draws itself by hand when it comes into view,
// like the animated Gmail avatar (snippets/bof-logo-draw.liquid). The still
// mark, an inline SVG inside the element, is what shows after the drawing,
// without JavaScript and to visitors who ask for reduced motion. The drawing
// comes from a map written by logo/export/animate.mjs in the brand kit of the
// bellofficina repo, a PNG holding for each pixel the pen travel at which the
// ink reaches it (red the high byte, green the low one) and how much of it the
// mark covers (blue). It arrives inside bellofficina-mark-draw.js as a data
// URL, never as an image asset: the Shopify CDN serves PNG assets to browsers
// as lossy WebP, which scrambles the times and draws the mark in scattered
// specks instead of with a pen. It draws once per page view.
if (!customElements.get('bof-logo-draw')) {
  customElements.define(
    'bof-logo-draw',
    class BofLogoDraw extends HTMLElement {
      // Same length and pace as the avatar: 2.6 s, mostly even speed with a
      // gentle start and finish, and a soft front of 6 px of pen travel.
      static DURATION = 2600;
      static FRONT = 6;
      static ease = (t) => 0.6 * t + 0.4 * (0.5 - 0.5 * Math.cos(Math.PI * t));

      connectedCallback() {
        if (!this.dataset.map || !('IntersectionObserver' in window)) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.classList.add('bof-logo-draw--waiting');
        this.near = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.near.disconnect();
            this.load();
          },
          { rootMargin: '600px 0px' }
        );
        this.near.observe(this);
      }

      disconnectedCallback() {
        this.near?.disconnect();
        this.seen?.disconnect();
        cancelAnimationFrame(this.frame);
      }

      load() {
        import(this.dataset.map)
          .then(({ default: source }) => {
            const map = new Image();
            map.onload = () => this.prepare(map);
            map.onerror = () => this.finish();
            map.src = source;
          })
          .catch(() => this.finish());
      }

      prepare(map) {
        const size = map.naturalWidth;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        canvas.setAttribute('aria-hidden', 'true');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(map, 0, 0);
        const data = context.getImageData(0, 0, size, size).data;
        context.clearRect(0, 0, size, size);

        const ink = (getComputedStyle(this).color.match(/[\d.]+/g) || [0, 0, 0]).map(Number);
        const image = context.createImageData(size, size);
        const pixels = [];
        const times = [];
        const covers = [];
        let total = 0;
        for (let i = 0; i < size * size; i++) {
          const cover = data[i * 4 + 2];
          if (!cover) continue;
          const time = data[i * 4] * 256 + data[i * 4 + 1];
          pixels.push(i * 4);
          times.push(time);
          covers.push(cover);
          if (time > total) total = time;
          image.data[i * 4] = ink[0];
          image.data[i * 4 + 1] = ink[1];
          image.data[i * 4 + 2] = ink[2];
        }
        Object.assign(this, { canvas, context, image, pixels, times, covers, total });
        this.append(canvas);

        this.seen = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.seen.disconnect();
            this.start = performance.now();
            this.frame = requestAnimationFrame((now) => this.draw(now));
          },
          { threshold: 0.6 }
        );
        this.seen.observe(this);
      }

      draw(now) {
        const { DURATION, FRONT, ease } = BofLogoDraw;
        const progress = Math.min(1, (now - this.start) / DURATION);
        const reach = ease(progress) * (this.total + FRONT);
        const out = this.image.data;
        for (let k = 0; k < this.pixels.length; k++) {
          const shown = Math.min(1, Math.max(0, (reach - this.times[k]) / FRONT + 1));
          out[this.pixels[k] + 3] = this.covers[k] * shown;
        }
        this.context.putImageData(this.image, 0, 0);
        if (progress < 1) this.frame = requestAnimationFrame((next) => this.draw(next));
        else this.finish();
      }

      finish() {
        this.canvas?.remove();
        this.classList.remove('bof-logo-draw--waiting');
      }
    }
  );
}
